import React, { Fragment, useCallback } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import {
  Grid,
  Progress,
  Segment,
  Header,
  Button,
  Checkbox,
  Divider,
  Label,
  Message,
  Modal,
} from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { ApplicationState } from 'rootReducer'
import { chunk, formatDate, formatDateTime } from 'common/Utils'
import { useBoolean, useParams, useResponsive } from 'common/hooks'
import { makeFailureSelector, makeLoadingSelector } from 'common/Loading/reducer'
import { getSavedCard } from 'common/UserContainer/reducer'
import { getServiceById } from 'pages/RechargePage/reducer'
import { Service } from 'pages/RechargePage/types'
import { updateRoamingStatus } from '../actions'
import { Addons, Entitlement, RoamingStatus } from '../types'
import { calculateUsedPercentage } from '../utils'

const CustomRow = styled(Grid.Row)`
  &&& {
    padding: 3.5px 0;
    align-items: center;
  }
`

const MessageRow = styled(Grid.Row)`
  &&& {
    padding-top: 0;
    margin-top: -0.5em;
  }
`

const CustomCol = styled(Grid.Column)`
  &&&& {
    padding-right: 0;
  }
`

const SubHeader = styled(Header.Subheader)`
  &&&&& {
    font-weight: normal;
    font-size: inherit;
    margin-top: 0.5em;
  }
`

const EntitlementEntry = ({ entitlements }: { entitlements: Entitlement[] }) => {
  return (
    <Fragment>
      {entitlements.map((entitlement) => (
        <CustomRow key={entitlement.name}>
          <CustomCol mobile={11} tablet={8} largeScreen={9} widescreen={9}>
            <Progress
              className="nomargin"
              color="teal"
              size={entitlements.length > 1 ? 'small' : undefined}
              percent={calculateUsedPercentage(
                entitlement.totalAllowance.amount,
                entitlement.remainingAllowance.amount
              )}
            />
          </CustomCol>
          <CustomCol mobile={5} tablet={8} largeScreen={7} widescreen={7}>
            {entitlement.remainingAllowance.amount}
            {entitlement.remainingAllowance.unit !== 'GB' && ' '}
            {entitlement.remainingAllowance.unit} remaining
          </CustomCol>
        </CustomRow>
      ))}
    </Fragment>
  )
}

const AddonCard = ({ addon }: { addon: Addons }) => {
  const endDate = addon.expiry_date || undefined

  return (
    <Grid.Column>
      <Segment className="noshadow">
        <Header as="h3">
          {addon.name}
          {endDate && <SubHeader>Expires on: {formatDate(new Date(endDate))}</SubHeader>}
        </Header>
        <Divider hidden />
        <Grid>
          <EntitlementEntry entitlements={addon.entitlements} />
        </Grid>
        <Divider hidden />
      </Segment>
    </Grid.Column>
  )
}

const TealToggle = styled(Checkbox)`
  &&& input:checked ~ label:before,
  &&& input:focus:checked ~ label:before {
    background-color: #39a486 !important;
  }
`

const ActiveLabel = styled(Label)`
  &&& {
    margin-left: 0.75em;
    vertical-align: middle;
  }
`

const RoamingToggleCard = ({
  service,
  roaming,
}: {
  service: Service
  roaming: RoamingStatus | null
}) => {
  const dispatch = useDispatch()
  const savedCard = useSelector(getSavedCard)
  const isUpdating = useSelector(makeLoadingSelector(['UPDATE_ROAMING']))
  const isFailed = useSelector(makeFailureSelector(['UPDATE_ROAMING']))
  const [isModalOpen, openModal, closeModal] = useBoolean(false)

  const enabled = service.roaming_enabled
  const dailyPrice = service.roaming_daily_price
  // A legacy roaming pack the customer already paid for covers their roaming
  // days, so the copy below must not promise them a daily charge.
  const onLegacyPack = service.has_legacy_roaming_pack

  const onToggle = useCallback(() => {
    if (enabled) {
      dispatch(updateRoamingStatus(service.uuid, false))
    } else {
      openModal()
    }
  }, [dispatch, enabled, service.uuid, openModal])

  const onConfirm = useCallback(() => {
    dispatch(updateRoamingStatus(service.uuid, true))
    closeModal()
  }, [dispatch, service.uuid, closeModal])

  const roamingDayActive = roaming && roaming.activated && roaming.ends_at

  return (
    <Grid.Column>
      <Segment className="noshadow">
        <Header as="h3">
          International Roaming
          {roamingDayActive && (
            <ActiveLabel color="teal" size="tiny" horizontal>
              {enabled
                ? 'Active'
                : `Active until ${formatDateTime(new Date(roaming.ends_at as string))}`}
            </ActiveLabel>
          )}
          {onLegacyPack ? (
            <SubHeader>Included with your legacy roaming pack</SubHeader>
          ) : (
            enabled && dailyPrice && <SubHeader>${dailyPrice} per day you roam</SubHeader>
          )}
        </Header>
        <TealToggle
          toggle
          checked={enabled}
          disabled={isUpdating || (!enabled && !savedCard)}
          onChange={onToggle}
          label={enabled ? 'Roaming on' : 'Roaming off'}
        />
        {onLegacyPack && (
          <Message info size="small">
            Roaming is included with your legacy roaming pack, so you are not charged per day. After
            the pack ends, you will be charged {dailyPrice ? `$${dailyPrice} ` : ''}for each day you
            roam.
          </Message>
        )}
        {!enabled && !savedCard && (
          <Message size="small">
            {onLegacyPack
              ? 'A saved card is required to turn on roaming for once your pack ends.'
              : 'A saved card is required to turn on roaming, as you are charged for each day you roam.'}
          </Message>
        )}
        {isFailed && (
          <Message negative size="small">
            Sorry, we could not update roaming. Please refresh and try again.
          </Message>
        )}
        <Modal size="large" centered open={isModalOpen} onClose={closeModal}>
          <Modal.Content>
            <Header as="h2">Turn on international roaming?</Header>
            {!onLegacyPack && service.has_recent_roaming_denial && !roamingDayActive && (
              <Message info size="small">
                It looks like you recently tried to use roaming. Turning this on will charge
                {dailyPrice ? ` $${dailyPrice}` : ' you'} for today&apos;s roaming straight away.
              </Message>
            )}
            <p>
              {onLegacyPack
                ? 'Roaming is included with your legacy roaming pack. After it ends, you will be charged '
                : 'You will be charged '}
              {dailyPrice ? `$${dailyPrice} to your saved card ` : ''}
              for each day you use roaming overseas. On days you roam, you can use your plan&apos;s
              data, calls and texts as if you were in New Zealand. You are only charged on days you
              actually use roaming.
            </p>
          </Modal.Content>
          <Modal.Actions
            actions={[
              <Button key="cancel" content="Cancel" onClick={closeModal} />,
              <Button key="confirm" positive content="TURN ON ROAMING" onClick={onConfirm} />,
            ]}
          />
        </Modal>
      </Segment>
    </Grid.Column>
  )
}

const keyPerRows = (addonsPerRow: Addons[]) => {
  let sumid = 0
  addonsPerRow.forEach((addon) => {
    sumid += Number(addon.id)
  })
  return sumid
}

const AddonTable = ({
  addons,
  roaming,
  apiDown,
}: {
  addons: Addons[]
  roaming: RoamingStatus | null
  apiDown: boolean
}) => {
  // Chunk Addons into a smaller list with length of 3
  // so that we can place it in the row of 3 cols
  const { isTablet, isMobile: onlyMobile } = useResponsive()
  const rowSize = isTablet ? 2 : 3
  const addonsPerRows = chunk(addons, rowSize)
  const { uuid } = useParams()
  const isMobile = isTablet || onlyMobile
  const service = useSelector<ApplicationState, Service | undefined>((state) =>
    getServiceById(state, uuid)
  )
  return (
    <Grid stackable columns={2}>
      <Grid.Row>
        <Grid.Column>
          <Header as="h2">Add-Ons</Header>
        </Grid.Column>
        {!isMobile && (
          <Grid.Column textAlign="right">
            <Button size="small" as={Link} to={`/services/${uuid}/addons/`} basic color="teal">
              PURCHASE ADD-ONS
            </Button>
          </Grid.Column>
        )}
      </Grid.Row>
      {apiDown && (
        <MessageRow>
          <Grid.Column>
            <p>Your Add-On usage is not available at the moment</p>
          </Grid.Column>
        </MessageRow>
      )}
      {service && service.is_xvne && (
        <Grid.Row columns={2}>
          <RoamingToggleCard service={service} roaming={roaming} />
        </Grid.Row>
      )}
      {addonsPerRows.map((addonsRow) => (
        <Grid.Row columns={rowSize} key={keyPerRows(addonsRow)}>
          {addonsRow.map((addon: Addons) => (
            <AddonCard key={addon.id} addon={addon} />
          ))}
        </Grid.Row>
      ))}
      {isMobile && (
        <Grid.Column width={16}>
          <Button as={Link} to={`/services/${uuid}/addons/`} basic fluid color="teal">
            PURCHASE ADD-ONS
          </Button>
        </Grid.Column>
      )}
    </Grid>
  )
}

export default AddonTable
